import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { Label, Stack } from '@bingads-webui-react/fabric-control';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { ValidationGroup, withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidatedFinalUrl as UrlInput } from '@bingads-webui-campaign-react/validated-url-input-fluent-v2';
import { IconButton } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { ThirdPartyMeasurementContainerWithValidation } from '@bingads-webui-campaign-react/third-party-measurement';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { Button, makeStyles } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { AdvancedSettings } from '@bingads-webui-campaign-react/display-plus-native-ads-advanced-settings';

import { ValidatedImageExperience } from './validated-image-experience';
import { getAdsStepStyles } from './styles';
import { CTAInCreativeEditor } from './validated-legacy-call-to-action';
import { useDisplayAdsCopilot } from './use-display-ads-copilot';

const useFluentV2Styles = makeStyles({
  button: {
    display: 'flex',
    'flex-flow': 'column',
  },
  addIcon: {
    color: '#0078D4',
    fontSize: '32px',
  },
});

const ValidatedFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(UrlInput))));

export const DisplayAdPanelWithCopilot = ({
  i18n,
  perfMarker,
  cid,
  aid,
  campaignId,
  onUpdate,
  onRemovePanel,
  removeCloseButton,
  permissions,
  displayAd,
  useBrandKit,
  keyIndex,
  isSelectedPanel,
  oDataConfig,
  customStyleFunction,
  useCustomStyleFunction,
  isLoadingImage,
}) => {
  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const customClasses = useDefaultStyles(customStyleFunction);

  const classes = useCustomStyleFunction ? customClasses : defaultClasses;

  const { generate, fetchAigcTemplateDefinition } = useDisplayAdsCopilot();

  const fluentV2Styles = useFluentV2Styles();

  const legacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);
  const i18nMerged = useMemo(() => mergeI18n(legacyI18n, i18n), [legacyI18n, i18n]);
  const finalUrlsObj = { FinalUrls: displayAd && displayAd.FinalUrls };

  const verifiedTrackingSettingObj = {
    VerifiedTrackingSettings: displayAd && displayAd.VerifiedTrackingSettings,
  };

  const isIasTrackingEnable = _.result(permissions.dynamic, 'EnableDisplayAdsIasTracking', false);
  const isAdvancedURLOptionsForMSANDisplayEnabled =
  _.result(permissions.dynamic, 'AdvancedURLOptionsForDisplay', false) &&
  _.result(permissions.dynamic, 'EnableMSANDisplayCampaigns', false);

  const isImpressionTrackingForDisplayAndVideoEnabled =
  _.result(permissions, 'isImpressionTrackingForDisplayAndVideoEnabled', false);


  const IsMOATEnabled = _.result(permissions, 'IsMOATEnabled', false);

  const onValidationUpdate = useCallback((result) => {
    onUpdate(result);
  }, [onUpdate]);

  const advancedSettings = useMemo(() => (
    <AdvancedSettings
      trackingUrlTemplate={displayAd && displayAd.TrackingUrlTemplate}
      finalUrlSuffix={displayAd && displayAd.FinalUrlSuffix}
      urlCustomParameters={displayAd && displayAd.UrlCustomParameters}
      verifiedTrackingSettings={verifiedTrackingSettingObj.VerifiedTrackingSettings}
      impressionTrackingUrls={displayAd && displayAd.ImpressionTrackingUrls}
      isDisplay
      isVideo={false}
      isAdvancedURLOptionsForMSANDisplayEnabled={isAdvancedURLOptionsForMSANDisplayEnabled}
      isAdvancedURLOptionsForMSANVideoEnabled={false}
    />
  ), [verifiedTrackingSettingObj.VerifiedTrackingSettings, displayAd, isAdvancedURLOptionsForMSANDisplayEnabled]);

  const onCTAUpdate = useCallback((data, setCTA) => {
    if (!_.isEmpty(data)) {
      setCTA(data);
    }
  }, []);


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const creativeEditorProps = useMemo(() => ({
    onRenderCTA: (newCTA, setCTA) => <CTAInCreativeEditor newCTA={newCTA} onValidate={(result) => { onCTAUpdate(result.data, setCTA); }} />,
    onApplyCreative: ({
      imageObj,
      creativeMeta,
    }) => {
      onUpdate({
        data: {
          ...displayAd,
          image: imageObj,
          creativeData: {
            creativeMeta,
          },
        },
        isValid: true,
        isDirty: true,
      });
    },
    finalUrl: displayAd && displayAd.FinalUrls && displayAd.FinalUrls[0],
    creativeData: displayAd && displayAd.creativeData,
    onGenerateCreative: creativeDataInner => generate({
      template: {
        ...creativeDataInner,
      },
    }),
    fetchAigcTemplateDefinition,
  }), [displayAd, generate, fetchAigcTemplateDefinition, onCTAUpdate, onUpdate]);

  const onRenderCustomAddImageButton = useCallback(({ setDisplayImagePicker }) => (
    <div className={classNames([classes.addNewImageButton, 'add-image'])}>
      <Button appearance="transparent" className={fluentV2Styles.button} onClick={() => setDisplayImagePicker(true)}>
        <AddRegular className={fluentV2Styles.addIcon} />
      </Button>
    </div>

  ), [classes.addNewImageButton, fluentV2Styles.addIcon, fluentV2Styles.button]);

  return (
    <div className={classNames([classes.displayAdPanelRoot, isSelectedPanel ? classes.selectedPanel : '', 'display-ad-panel-root'])}>
      <ValidationGroup
        validationProps={{
          schema: ResponsiveAd,
          onValidate: onValidationUpdate,
        }}
        key={`${keyIndex}-validationGroup`}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          { !removeCloseButton &&
            <div className={classes.displayAdPanelHeader}>
              <IconButton iconProps={{ iconName: 'Cancel' }} title="Remove ad" className="remove-ad-panel" onClick={onRemovePanel} />
            </div>
          }
          <ValidatedFinalUrl
            containerClassName={classNames(classes.displayAdPanelFinalUrlContainer, 'final-url-container')}
            i18n={i18n}
            validationProps={{
              data: finalUrlsObj,
              initialValue: finalUrlsObj,
            }}
            validateOnRender
          />
          <div>
            <div className={classes.displayImageLabel}>
              <Label>
                <Stack horizontal verticalAlign="center" >
                  {i18n.getString(_TL_('Creative'))}
                  <HelpPopupInline topic="pop_BA_DisplayAds_UserImages" />
                </Stack>
              </Label>
            </div>
            <ValidatedImageExperience
              i18n={i18nMerged}
              perfMarker={perfMarker}
              permissions={permissions}
              oDataConfig={oDataConfig}
              cid={cid}
              aid={aid}
              campaignId={campaignId}
              disableRemoveAll
              hideRatioLabel
              displayAd={displayAd}
              useBrandKit={useBrandKit}
              key={`${keyIndex}-ImageExperience`}
              idx={keyIndex}
              ImageExperienceCircleUploadType
              disableUploadTextField
              ImagePickerContainerHeight="auto"
              isLoadingImage={isLoadingImage}
              creativeEditorProps={(displayAd && displayAd.creativeData) ? creativeEditorProps : null}
              onRenderCustomAddImageButton={onRenderCustomAddImageButton}
            />
          </div>
          { !(isAdvancedURLOptionsForMSANDisplayEnabled || isImpressionTrackingForDisplayAndVideoEnabled) && isIasTrackingEnable && (
          <ThirdPartyMeasurementContainerWithValidation
            i18n={i18n}
            videoAd={displayAd}
            validationProps={{
                  data: verifiedTrackingSettingObj,
                  initialValue: verifiedTrackingSettingObj,
                }}
            adType="display"
            IsMOATEnabled={IsMOATEnabled}
          />
            )}
          { (isAdvancedURLOptionsForMSANDisplayEnabled || isImpressionTrackingForDisplayAndVideoEnabled) && (
            <div>
              {advancedSettings}
            </div>
          )}
        </Stack>
      </ValidationGroup>
    </div>
  );
};

DisplayAdPanelWithCopilot.propTypes = {
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      EnableDisplayAdsIasTracking: PropTypes.bool,
    }),
  }).isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  campaignId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemovePanel: PropTypes.func.isRequired,
  removeCloseButton: PropTypes.bool.isRequired,
  displayAd: PropTypes.shape([]).isRequired,
  useBrandKit: PropTypes.bool,
  keyIndex: PropTypes.number.isRequired,
  isSelectedPanel: PropTypes.bool.isRequired,
  oDataConfig: PropTypes.shape({}),
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
  isLoadingImage: PropTypes.bool.isRequired,
};

DisplayAdPanelWithCopilot.defaultProps = {
  oDataConfig: null,
  customStyleFunction: _.noop,
  useCustomStyleFunction: false,
};
